import { Injectable } from '@angular/core';
import { ConnectionService } from '../connection/connection.service';
import { ParseService } from './parse.service';
import { Models } from '@ibexfusion/models';
import { ContentEntry } from '@ibexfusion/models/common/models';
import * as _ from 'lodash';
import { StorageService } from '../storage/storage.service';
import { v4 as uuid } from 'uuid';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ContentService {

  public metaPackage: any;
  public mainPackage: any;
  private packageId = window['forcePackage'];
  private acceptedVideoFileGroups = ['videos', 'trailers'];
  private acceptedImageFileGroups = ['images', 'pictures', 'picture', 'posters'];

  private packageCache: any = {};

  constructor(private connection: ConnectionService, private parseService: ParseService, private storage: StorageService) {
  }

  private static getPublishing(entry) {
    if (entry && entry.detail) {
      return entry.detail.ibexPublishing;
    } else {
      return null;
    }
  }

  private static weekdaysValid(weekdays): boolean {
    if (weekdays === null || weekdays === undefined) {
      return true;
    }

    const days = [];
    if (!Array.isArray(weekdays)) {
      weekdays = _.values(weekdays);
    }

    for (const weekday of weekdays) {
      days[weekday] = true;
    }

    return days[new Date().getDay()];
  }

  public initMeta() {
    return this.connection.find('/content/getPackageMeta/' + this.packageId).then(jsonMeta => {
      this.metaPackage = jsonMeta;
      return jsonMeta;
    });
  }

  public async loadPackage(language: string, languages?: string[], force = false) {
    if (!this.packageCache?.timestamp || !!force) {
      const promises = (languages || ['de']).map((l: string) => {
        return this.connection.find(`/content/getPackageData/${this.packageId}/${l}/s/${new Date().getTime()}`).then(pak => {
          this.packageCache.timestamp = new Date().getTime();
          this.packageCache[l] = pak;
        }).catch(err => {
          console.warn('language file not found for ', l);
        });
      });
      await Promise.all(promises);
    }
    this.mainPackage = this.parseService.parsePackageJSON(this.packageCache[language], language);
    return this.mainPackage;
  }

  public loadPackageLog() {
    if (!(!environment.production || !!window['disableAnalytics'])) {
      return this.connection.find('/content/getPackageLog/' + this.packageId);
    } else {
      let deviceId = this.storage.get('deviceId');
      if (!deviceId) {
        deviceId = uuid();
        this.storage.set('deviceId', deviceId);
      }
      return this.connection.find('/content/getPackageLog/' + this.packageId + '/' + deviceId + '/ibextouch');
    }
  }

  public loadSecuredContentApp(menuItem, profile): Promise<any> {
    const data = {
      FrontProfileID: profile.FrontProfileID,
      packageInstalledAppId: menuItem.packageInstalledAppId,
      language: profile.currentLanguage
    };
    return this.connection.post('/content/getSecuredApp', data).then(securedContent => {
      if (securedContent && securedContent?.menu?.items) {
        const packageApp = this.parseService.parsePackageApp(securedContent);
        menuItem = packageApp.menuItems[menuItem._id];
        if (menuItem?.entry && packageApp.entries[menuItem.entry]) {
          if (this.mainPackage.ticker && this.mainPackage.ticker.item.isSecured) {
            this.mainPackage.ticker = this.parseService.renewTicker(this.mainPackage.ticker, packageApp);
          }
          this.mainPackage.entries = this.parseService.mergeAndOverwriteItems(this.mainPackage.entries, packageApp.entries);
          this.mainPackage.menuItems = this.parseService.mergeAndOverwriteItems(this.mainPackage.menuItems, packageApp.menuItems);
          // todo mergeAndOverwriteFiles @raphi
          return Promise.resolve(this.mainPackage);
        }
      }
      console.log('could not fetch secured content', menuItem);
      return Promise.resolve(this.mainPackage);
    }).catch(err => {
      console.log('could not fetch secured content: ', err);
      return Promise.resolve(this.mainPackage);
    });
  }

  public getById(itemId: string): Models.ContentEntry | Models.MenuItem | Models.ContentFile {
    if (!this.mainPackage) {
      return null;
    }
    if (this.mainPackage && this.getEntry(itemId)) {
      return this.getEntry(itemId);
    }
    if (this.mainPackage && this.getFile(itemId)) {
      return this.getFile(itemId);
    }
    if (this.mainPackage && this.getMenuItem(itemId)) {
      return this.getMenuItem(itemId);
    }
    return null;
  }

  public getEntry(entryId: string): Models.ContentEntry {
    const entry = this.mainPackage && this.mainPackage.entries ? this.mainPackage.entries[entryId] : null;
    return this.isValid(entry) ? entry : null;
  }

  public getEntryParent(id) {
    for (const key in this.mainPackage.entries) {
      if (this.mainPackage.entries.hasOwnProperty(key) && this.mainPackage.entries[key].related?.entries) {
        for (const related of this.mainPackage.entries[key].related.entries) {
          if (related.id === id && this.isValid(this.mainPackage.entries[key])) {
            return this.mainPackage.entries[key];
          }
        }
      }
    }
    return null;
  }

  public getMenuItem(itemId: string): Models.MenuItem {
    return this.mainPackage?.menuItems?.[itemId];
  }

  /* get functions used by content pipes */
  public getFile(itemId: string): Models.ContentFile {
    return this.mainPackage?.files?.[itemId];
  }

  public getWidgetByAppId(appId) {
    const cleanAppId = appId.replace('_App', '').replace('item_', '');
    for (const widget of this.mainPackage.menuItems.itemWidgets.children) {
      if (widget.id.includes(appId) || widget.id.includes(cleanAppId)) {
        return this.mainPackage.menuItems[widget.id];
      }
    }
    return null;
  }

  public getAppByWidgetId(widgetId: string) {
    for (const app of this.mainPackage.menuItems.itemApps.children) {
      let id = widgetId.replace(/(_Widget|_item_widgetItem|_widgetItem|item_)/gm, '');
      id = id.slice(id.search(/\d/));
      if (app.id.indexOf(id) > -1) {
        return this.mainPackage.menuItems[app.id];
      }
    }
    return null;
  }

  public getImageUrl(entry, defaultValue: string) {
    if (entry && entry.files && entry.files.length) {
      for (const file of entry.files) {
        if (file.group === 'images') {
          return this.getFile(file.id)?.url;
        }
      }
    }
    return defaultValue;
  }

  public getAssetByUrl(url) {
    return Object.keys(this.mainPackage?.files).map(key => this.mainPackage?.files?.[key]).find(file => file?.url?.includes(url));
  }

  getMediaEntriesForEntry(entryId: string): Models.MediaEntry[] {
    let entry = this.getEntry(entryId);
    if (!entry?.files?.length && entry?.related?.entries?.length === 1 && this.getEntry(entry.related.entries[0].id)?.files?.length) {
      entry = this.getEntry(entry.related.entries[0].id);
    }
    const mediaEntries: Models.MediaEntry[] = [];
    if (!entry?.files) {
      return mediaEntries;
    }
    for (const item of entry.files) {
      const file = this.getFile(item.id);
      if (!file || !file.url || (this.acceptedVideoFileGroups.indexOf(item.group) === -1 && this.acceptedImageFileGroups.indexOf(item.group) === -1)) {
        continue;
      }
      const fileType = this.acceptedVideoFileGroups.indexOf(item.group) > -1 ? 'video' : 'image';
      const mediaEntry = new Models.MediaEntry();
      mediaEntry.populateFromJSON({ URL: file?.url, type: fileType });
      mediaEntries.push(mediaEntry);
    }
    return mediaEntries;
  }

  getDocumentEntriesForEntry(entryId: string): Models.InformationDocumentEntry[] {
    const entry = this.getEntry(entryId);
    const documentEntries: Models.InformationDocumentEntry[] = [];
    if (!entry || !entry.files) {
      return documentEntries;
    }
    for (const item of entry.files) {
      const file = this.getFile(item.id);
      if (!file || !file.url || (item.group !== 'pdfs' && item.group !== 'pdf')) {
        continue;
      }
      const informationDocumentEntry = new Models.InformationDocumentEntry();
      informationDocumentEntry.populateFromJSON({ title: file.name, url: file?.url, type: 'pdf', extension: 'pdf' });
      documentEntries.push(informationDocumentEntry);
    }
    return documentEntries;
  }

  getFirstImageFileForEntry(entryId: string): Models.ContentFile { // returns first in group images
    const entry = this.getEntry(entryId);
    if (!entry || !entry.files) {
      return null;
    }
    for (const item of entry.files) {
      if (item.group === 'images' || item.group === 'pictures' || item.group === 'picture') {
        return this.getFile(item.id);
      }
    }
    return null;
  }

  /* check valid ibex publishing */

  public isValid(entry: ContentEntry) {

    const publishing = ContentService.getPublishing(entry);

    if (!publishing) { // if entry has no publishing info, it is valid
      return true;
    }
    const now = new Date();

    if (!ContentService.weekdaysValid(publishing.weekdays)) {
      return false;
    }
    if (!!publishing.publishstart && typeof publishing.publishstart === 'string') { // convert value for new dynamic-content
      if (/\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d.\d\d\d/gm.test(publishing.publishstart)) {
        publishing.publishstart = new Date(publishing.publishstart).getTime() / 1000;
      }
    }

    if (!!publishing.publishfinish && typeof publishing.publishfinish === 'string') { // convert value for new dynamic-content
      if (/\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d.\d\d\d/gm.test(publishing.publishfinish)) {
        publishing.publishfinish = new Date(publishing.publishfinish).getTime() / 1000;
      }
    }

    if (!!publishing.publishstart && new Date(publishing.publishstart * 1000).getTime() > now.getTime()) {
      return false;
    }

    if (!!publishing.publishfinish && new Date(publishing.publishfinish * 1000).getTime() < now.getTime()) {
      return false;
    }

    if (publishing.timeframestart && publishing.timeframefinish) {
      if (parseInt(publishing.timeframestart.replace(':', '')) > parseInt(publishing.timeframefinish.replace(':', ''))) {
        const currentTime = new Date();
        const startTime = new Date(currentTime.getTime());
        startTime.setHours(publishing.timeframestart.split(':')[0]);
        startTime.setMinutes(publishing.timeframestart.split(':')[1]);
        startTime.setSeconds(0);
        startTime.setDate(startTime.getDate() - 1);
        const endTime = new Date(currentTime.getTime());
        endTime.setHours(publishing.timeframefinish.split(':')[0]);
        endTime.setMinutes(publishing.timeframefinish.split(':')[1]);
        endTime.setSeconds(0);
        return startTime < currentTime && endTime > currentTime;
      }
    }

    if (!!publishing.timeframestart) {
      const tfs = publishing.timeframestart.split(':');

      if (+tfs[0] > now.getHours()) {
        return false;
      }
      if (+tfs[0] === now.getHours() && +tfs[1] > now.getMinutes()) {
        return false;
      }
    }

    if (!!publishing.timeframefinish) {
      const tff = publishing.timeframefinish.split(':');

      if (+tff[0] < now.getHours()) {
        return false;
      }
      if (+tff[0] === now.getHours() && +tff[1] < now.getMinutes()) {
        return false;
      }
    }

    return true;
  }
}
